// src/features/dashboard/components/HeroDashboardHeader.tsx
import { Sparkles } from 'lucide-react';
import { Skeleton } from './ui/Skeleton';
import { useLiveClock } from '../hooks/useLiveClock';
import type { Representative } from '../types/dashboard.types';

interface HeroDashboardHeaderProps {
  representative: Representative | null;
  isInitialLoading: boolean;
}

function timeOfDayGreeting(hour: number): string {
  if (hour < 5) return 'شب بخیر';
  if (hour < 12) return 'صبح بخیر';
  if (hour < 18) return 'ظهر بخیر';
  return 'عصر بخیر';
}

/**
 * Premium hero header for the top of the Representative Dashboard.
 *
 * Scope note: an "account status" concept (active / suspended / trial)
 * does not exist anywhere in the current data contract -- `Representative`
 * only carries `isOnline`. Rather than fabricate a status field, this
 * component keeps the existing, real online/offline presence indicator
 * on the avatar and does not render a separate "account status badge".
 *
 * All other content (greeting, Persian date, representative name) is
 * sourced from `useLiveClock` and the `representative` prop -- no mock
 * or derived values.
 *
 * Entrance and hover motion are handled entirely by existing CSS (the
 * shared `[data-animate]` fade-in keyframe and `.vania-glass-card--interactive`
 * hover lift), both of which already respect `prefers-reduced-motion` --
 * no JS animation library involved.
 */
export function HeroDashboardHeader({ representative, isInitialLoading }: HeroDashboardHeaderProps) {
  const { dateLabel, timeLabel, now } = useLiveClock();
  const greeting = timeOfDayGreeting(now.getHours());

  return (
    <div
      className="vania-glass-card vania-glass-card--interactive vania-glass-card--elevated vania-hero-header relative overflow-hidden p-5 sm:p-6"
      data-animate="fade-in"
    >
      {/* signature ambient network-pulse mesh, restyled with a wider gradient spread */}
      <div className="vania-hero-mesh" aria-hidden="true" />

      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            {isInitialLoading || !representative ? (
              <Skeleton className="h-14 w-14 sm:h-16 sm:w-16" rounded="full" />
            ) : (
              <>
                <img
                  src={representative.avatarUrl}
                  alt={representative.displayName}
                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-full ring-2 ring-[var(--vania-border-glass,rgba(255,255,255,0.12))] object-cover bg-[var(--vania-surface-2,rgba(255,255,255,0.04))]"
                />
                <span
                  className={
                    'absolute -bottom-0.5 -end-0.5 h-4 w-4 rounded-full ring-[3px] ring-[var(--vania-colors-background-primary,#0e0f12)] ' +
                    (representative.isOnline
                      ? 'bg-[var(--vania-accent-teal,#2fe0c0)] vania-online-pulse'
                      : 'bg-[var(--vania-text-tertiary,#6b7280)]')
                  }
                  aria-label={representative.isOnline ? 'آنلاین' : 'آفلاین'}
                />
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            {isInitialLoading || !representative ? (
              <>
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-48" />
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--vania-accent-violet,#7c6cff)]">
                  <Sparkles size={13} strokeWidth={2.5} />
                  {representative.role}
                </span>
                <h1 className="vania-hero-title text-xl sm:text-2xl font-bold text-[var(--vania-text-primary,#f5f6fa)] tracking-tight">
                  {greeting}، {representative.displayName} 👋
                </h1>
                <p className="text-sm font-medium text-[var(--vania-text-secondary,#a3aab8)]">
                  {representative.isOnline ? representative.lastSeenLabel : 'آفلاین'}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-start sm:items-end gap-1 shrink-0 rtl:text-right">
          <span className="text-2xl font-bold tabular-nums text-[var(--vania-text-primary,#f5f6fa)]">
            {dateLabel}
          </span>
          <span className="text-sm text-[var(--vania-text-secondary,#a3aab8)]">{timeLabel}</span>
        </div>
      </div>
    </div>
  );
}
